export type BlacklistAllianceCheckKey = "phone_check" | "email_filter";

export interface BlacklistAllianceCheckConfig {
  enabled: boolean;
  intake: boolean;
}

export interface BlacklistAllianceConfig {
  phoneCheck: BlacklistAllianceCheckConfig;
  emailFilter: BlacklistAllianceCheckConfig;
}

export const BLACKLIST_EMAIL_FILTER_UNAVAILABLE_STATUS =
  "Awaiting provider response-contract verification";

export const BLACKLIST_EMAIL_FILTER_UNAVAILABLE_EXPLANATION =
  "Blacklist Alliance documents the single-email endpoint but does not currently define which response status represents a suppressed email. The filter cannot be activated safely until that contract is verified.";

export const BLACKLIST_EMAIL_FILTER_ENABLED_WARNING =
  "This stored setting is enabled but blocked. Email-bearing leads will fail closed into review until the setting is disabled or the provider contract is verified.";

export const DEFAULT_BLACKLIST_ALLIANCE_PHONE_CACHE_CONFIG = {
  enabled: true,
  clean_ttl_days: 30,
  blacklisted_ttl_days: 180,
  error_backoff_seconds: 900,
} as const;

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function resolveCheck(
  value: unknown,
): BlacklistAllianceCheckConfig | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const check = value as Record<string, unknown>;
  return {
    enabled: check.enabled === true,
    intake: check.intake !== false,
  };
}

export function getBlacklistAllianceConfig(
  config: Record<string, unknown> | null,
): BlacklistAllianceConfig {
  const services = asRecord(config?.services);
  const service = asRecord(services.blacklist_alliance);

  return {
    phoneCheck: resolveCheck(service.phone_check) ?? {
      enabled: service.enabled === true,
      intake: service.intake !== false,
    },
    emailFilter: resolveCheck(service.email_filter) ?? {
      enabled: false,
      intake: true,
    },
  };
}

export function updateBlacklistAllianceCheck(
  config: Record<string, unknown>,
  checkKey: BlacklistAllianceCheckKey,
  patch: Partial<BlacklistAllianceCheckConfig>,
): Record<string, unknown> {
  const next = structuredClone(config);
  const services = asRecord(next.services);
  next.services = services;
  const existingService = asRecord(services.blacklist_alliance);
  services.blacklist_alliance = existingService;
  const resolved = getBlacklistAllianceConfig(next);
  const existingPhone = asRecord(existingService.phone_check);
  const existingEmail = asRecord(existingService.email_filter);

  existingService.phone_check = {
    ...existingPhone,
    enabled: resolved.phoneCheck.enabled,
    intake: resolved.phoneCheck.intake,
    ...(checkKey === "phone_check" ? patch : {}),
  };
  existingService.email_filter = {
    ...existingEmail,
    enabled: resolved.emailFilter.enabled,
    intake: resolved.emailFilter.intake,
    ...(checkKey === "email_filter" ? patch : {}),
  };

  return next;
}

export function updateBlacklistAllianceEmailFilterFromUi(
  config: Record<string, unknown>,
  enabled: boolean,
): Record<string, unknown> {
  const current = getBlacklistAllianceConfig(config).emailFilter.enabled;
  if (!current && enabled) return structuredClone(config);
  return updateBlacklistAllianceCheck(config, "email_filter", { enabled });
}

export function getDefaultBlacklistAllianceServiceConfig() {
  return {
    phone_check: {
      enabled: false,
      intake: true,
      cache: { ...DEFAULT_BLACKLIST_ALLIANCE_PHONE_CACHE_CONFIG },
    },
    email_filter: {
      enabled: false,
      intake: true,
    },
  };
}
